'use client';

import * as React from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import { SectionHeading } from '@/components/frontend/common/section-heading';

interface DemoFaq {
    question: string;
    answer: string;
}

export function DemoFaqs({ initialFaqs }: { initialFaqs?: DemoFaq[] } = {}) {
    const t = useTranslations('demoPage');
    const locale = useLocale();
    const [faqs, setFaqs] = React.useState<DemoFaq[]>(() => initialFaqs ?? []);
    const [isLoadingFaqs, setIsLoadingFaqs] = React.useState(!initialFaqs?.length);
    const [openId, setOpenId] = React.useState<string>('');

    React.useEffect(() => {
        if (initialFaqs?.length) {
            setFaqs(initialFaqs);
            setIsLoadingFaqs(false);
            return;
        }
        const fetchDemoFaqs = async () => {
            try {
                setIsLoadingFaqs(true);
                const res = await fetch(`/api/demo/faq?locale=${encodeURIComponent(locale)}`, {
                    cache: 'no-store',
                });

                if (!res.ok) {
                    throw new Error('Failed to fetch FAQs');
                }

                const data = await res.json();
                if (data?.items && Array.isArray(data.items) && data.items.length > 0) {
                    setFaqs(data.items);
                } else {
                    const staticFaqs = t.raw('faqs.items') as unknown;
                    const fallbackFaqs = Array.isArray(staticFaqs)
                        ? (staticFaqs as { question: string; answer: string }[])
                        : [];
                    setFaqs(fallbackFaqs);
                }
            } catch (error) {
                try {
                    const staticFaqs = t.raw('faqs.items') as unknown;
                    const fallbackFaqs = Array.isArray(staticFaqs)
                        ? (staticFaqs as { question: string; answer: string }[])
                        : [];
                    setFaqs(fallbackFaqs);
                } catch {
                    setFaqs([]);
                }
            } finally {
                setIsLoadingFaqs(false);
            }
        };

        fetchDemoFaqs();
    }, [locale, t, initialFaqs?.length]);

    const toggle = (id: string) => setOpenId((prev) => (prev === id ? '' : id));

    return (
        <section className="bg-gray-50 dark:bg-gray-950">
            <div className="container mx-auto px-4 py-16 md:py-20">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-8">
                        <SectionHeading
                            page="demo"
                            align="center"
                            maxWidthClass="max-w-4xl"
                            fallbackPill={t('faqs.pill')}
                            fallbackTitle={t('faqs.title')}
                            fallbackSubtitle=""
                            titleVariant="h2"
                        />
                    </div>

                    <div className="space-y-4">
                        {isLoadingFaqs ? (
                            <div className="text-center py-8 text-gray-400">{t('faqs.loading', { default: 'Loading FAQs...' })}</div>
                        ) : faqs.length > 0 ? (
                            faqs.map((faq, index) => {
                                const id = `demo-faq-${index}`;
                                const isOpen = openId === id;
                                return (
                                    <div
                                        key={id}
                                        className={cn(
                                            'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#1a1f2a] text-left shadow-lg transition-all duration-300 ease-out',
                                            isOpen && 'border-primary-600 shadow-primary-500/15'
                                        )}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggle(id)}
                                            className="w-full flex items-center justify-between px-6 py-5 text-left gap-4 border-b border-gray-100 dark:border-gray-800/80"
                                            aria-expanded={isOpen}
                                        >
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {faq.question}
                                            </h3>
                                            {isOpen ? (
                                                <ChevronUp className="h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-300" />
                                            ) : (
                                                <ChevronDown className="h-5 w-5 text-gray-500 dark:text-gray-400 transition-transform duration-300" />
                                            )}
                                        </button>
                                        <div
                                            className={cn(
                                                'px-6 text-gray-700 dark:text-gray-300 leading-relaxed transition-all duration-300 ease-out pt-3',
                                                isOpen ? 'max-h-48 opacity-100 pb-5' : 'max-h-0 opacity-0 pb-0'
                                            )}
                                            style={{ overflow: 'hidden' }}
                                        >
                                            {faq.answer}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center py-8 text-gray-400">
                                {t('faqs.noItems', { default: 'No FAQs available.' })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

